/**
 * 資源撫平的降級包裝層（與 `store/scheduling.ts` 同樣的契約：永不拋錯）。
 *
 * 撫平在中型專案上約需 1～2 秒，是本 app 唯一「使用者感覺得到」的同步運算。
 * 因此這裡刻意只提供**純函式**，把「什麼時候跑」交給呼叫端 —— UI 必須先把
 * `isRunning` 打開、讓瀏覽器畫一次進行中狀態，再於下一個 macrotask 呼叫
 * `runLeveling()`，否則整段運算會與 state 更新擠在同一幀，畫面直接凍結兩秒
 * 而且完全沒有回饋。
 */

import {
  applyLeveling as coreApplyLeveling,
  level as coreLevel,
  DEFAULT_LEVELING_OPTIONS,
  type Diagnostic,
  type LevelingOptions,
  type LevelingResult,
  type Project,
} from '@bigint-project/core';

export interface LevelingRunResult {
  readonly project: Project;
  readonly result: LevelingResult | null;
  readonly diagnostics: readonly Diagnostic[];
  readonly error: string | null;
}

export interface RunLevelingDeps {
  readonly level: (input: { project: Project; options: LevelingOptions }) => LevelingResult;
  readonly applyLeveling: (project: Project, result: LevelingResult) => Project;
}

const defaultDeps: RunLevelingDeps = {
  level: coreLevel,
  applyLeveling: coreApplyLeveling,
};

function toMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  try {
    return String(error);
  } catch {
    return 'Unknown error (failed to stringify)';
  }
}

export function runLeveling(
  project: Project,
  options: LevelingOptions = DEFAULT_LEVELING_OPTIONS,
  deps: RunLevelingDeps = defaultDeps,
): LevelingRunResult {
  try {
    const result = deps.level({ project, options });
    const nextProject = deps.applyLeveling(project, result);
    return { project: nextProject, result, diagnostics: result.diagnostics, error: null };
  } catch (error) {
    return {
      project,
      result: null,
      diagnostics: [
        {
          severity: 'Warning',
          code: 'LEVELING_ENGINE_UNAVAILABLE',
          message: `資源撫平失敗，未套用任何變更：${toMessage(error)}`,
        },
      ],
      error: toMessage(error),
    };
  }
}

export { DEFAULT_LEVELING_OPTIONS };
export type { LevelingOptions, LevelingResult };
